/**
 * Small presentational primitives.
 *
 * Everything renders as text or semantic HTML - no dangerouslySetInnerHTML is
 * used anywhere in this application, so model output can never become markup.
 */

import type { ReactNode } from 'react';

/* -------------------------------------------------------------------------- */
/* Alerts                                                                      */
/* -------------------------------------------------------------------------- */

export interface AlertProps {
  tone?: 'error' | 'warning' | 'success' | 'info';
  title?: string;
  children?: ReactNode;
  role?: 'alert' | 'status';
}

export function Alert({ tone = 'info', title, children, role }: AlertProps) {
  const resolvedRole = role ?? (tone === 'error' ? 'alert' : 'status');
  return (
    <div className={`alert alert--${tone}`} role={resolvedRole}>
      <div className="alert__body">
        {title ? <div className="alert__title">{title}</div> : null}
        {children}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Badges                                                                      */
/* -------------------------------------------------------------------------- */

export function Badge({
  kind,
  children,
}: {
  kind?: 'confirmed' | 'likely' | 'speculative' | 'pro' | 'error' | 'default';
  children: ReactNode;
}) {
  const suffix = kind && kind !== 'default' ? ` badge--${kind}` : '';
  return <span className={`badge${suffix}`}>{children}</span>;
}

export function KindBadge({ kind }: { kind: 'confirmed' | 'likely' | 'speculative' }) {
  return <Badge kind={kind}>{kind}</Badge>;
}

/* -------------------------------------------------------------------------- */
/* Feedback                                                                    */
/* -------------------------------------------------------------------------- */

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="row" style={{ gap: 8 }}>
      <span className="spinner" aria-hidden="true" />
      <span className="sr-only">{label ?? 'Loading'}</span>
    </span>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty-state">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  help,
  children,
}: {
  label: string;
  htmlFor: string;
  help?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {help ? (
        <div className="field__help" id={`${htmlFor}-help`}>
          {help}
        </div>
      ) : null}
    </div>
  );
}

export function Stat({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Code                                                                        */
/* -------------------------------------------------------------------------- */

export function CodeBlock({
  code,
  language,
  title,
  actions,
  maxHeight,
}: {
  code: string;
  language?: string;
  title?: string;
  actions?: ReactNode;
  maxHeight?: number;
}) {
  return (
    <div className="code-block">
      {(title || actions || language) && (
        <div className="code-block__header">
          <span className="mono faint">{title ?? language ?? 'code'}</span>
          <span className="row" style={{ gap: 8 }}>
            {actions}
          </span>
        </div>
      )}
      <pre style={maxHeight ? { maxHeight } : undefined} tabIndex={0}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Quota                                                                       */
/* -------------------------------------------------------------------------- */

import type { QuotaStatus } from '@core/types';

export function QuotaMeter({ quota }: { quota: QuotaStatus | null | undefined }) {
  if (!quota) return null;
  const critical = quota.remaining === 0;
  return (
    <span className="row small" style={{ gap: 8 }}>
      <Badge kind={critical ? 'error' : 'default'}>
        {quota.used}/{quota.limit} this month
      </Badge>
      <span className="faint">
        {critical
          ? 'Quota reached — resets at the start of next month (UTC)'
          : `${quota.remaining} remaining`}
      </span>
    </span>
  );
}
